/**
 * In-view animations — zero dependency, IntersectionObserver based.
 * SCSS classes live in resources/assets/styles/common/_animations.scss.
 *
 * ── Data attributes ────────────────────────────────────────────────────────
 *
 * data-animate="<variant>"          Required. Adds initial transform + opacity.
 *   fly-l-r                         Slides in from the left  → right
 *   fly-r-l                         Slides in from the right ← left
 *   fly-d-u                         Slides in from below     ↑ up
 *
 * data-animate-delay="<ms>"         Delay before animation starts (default: 0).
 *   e.g. data-animate-delay="200"
 *
 * data-animate-duration="<ms>"      Animation duration (default: 820).
 *   e.g. data-animate-duration="600"
 *
 * data-animate-move="<length>"      How far the element travels (default: 3.5rem).
 *   e.g. data-animate-move="5rem"   Accepts: px, rem, em, vw, vh, %
 *
 * data-animate-fade="false"         Disable fade-in; only position animates.
 *   (CSS handles this — no JS change needed)
 *
 * data-animate-once="false"         Re-animate every time element re-enters
 *                                   the viewport (default: true = animate once).
 *
 * ── Examples ───────────────────────────────────────────────────────────────
 *
 *   <div data-animate="fly-d-u"></div>
 *   <div data-animate="fly-l-r" data-animate-delay="200" data-animate-duration="600"></div>
 *   <div data-animate="fly-r-l" data-animate-fade="false" data-animate-move="5rem"></div>
 *   <div data-animate="fly-d-u" data-animate-once="false"></div>
 */

// ── Constants ───────────────────────────────────────────────────────────────

const SELECTOR = '[data-animate]';
const ACTIVE_CLASS = 'is-inview';
const DEFAULT_DURATION = 820; // ms
const DEFAULT_MOVE = '3.5rem';

// ── Helpers ─────────────────────────────────────────────────────────────────

const getDelay = (element) => {
  const raw = Number.parseInt(element.dataset.animateDelay || '0', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : 0;
};

const getDuration = (element) => {
  const raw = Number.parseInt(element.dataset.animateDuration || '', 10);
  return Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_DURATION;
};

const getMovement = (element) => {
  const raw = (element.dataset.animateMove || '').trim();

  // Accept values like: 72px, 4rem, 8vw, 10%.
  if (/^-?\d*\.?\d+(px|rem|em|vw|vh|%)$/.test(raw)) {
    return raw;
  }

  return DEFAULT_MOVE;
};

const shouldAnimateOnce = (element) => element.dataset.animateOnce !== 'false';

const prepareElementStyles = (element) => {
  const duration = getDuration(element);
  const move = getMovement(element);

  element.style.setProperty('--animate-duration', `${duration}ms`);
  element.style.setProperty('--animate-move', move);
};

// ── Core ─────────────────────────────────────────────────────────────────────

const reveal = (element) => {
  const delay = getDelay(element);
  const duration = getDuration(element);

  // Ensure custom properties exist before class changes.
  prepareElementStyles(element);
  // Also set transitionDuration directly so the value is picked up even when
  // the CSS custom property and the class addition happen in the same frame.
  element.style.transitionDuration = `${duration}ms, ${duration}ms`;
  element.style.transitionDelay = delay > 0 ? `${delay}ms` : '';

  // rAF ensures the browser has painted the initial (hidden) state before
  // adding is-inview, so the transition always fires reliably.
  requestAnimationFrame(() => {
    element.classList.add(ACTIVE_CLASS);
  });
};

const hide = (element) => {
  element.classList.remove(ACTIVE_CLASS);
};

const initIframeOverlay = () => {
  console.log('Initializing iframe overlay for store locator maps');
  if (typeof window.jQuery === 'undefined') {
    return;
  }

  const $ = window.jQuery;
  const $iframes = $('.wpsl-gmap-canvas').not('#noOverlay');

  $iframes.each((_, iframe) => {
    const $iframe = $(iframe);
    if (!$iframe.parent().hasClass('overlayiFrame')) {
      $iframe.wrap('<div class="overlayiFrame" />');
    }
  });

  $('.overlayiFrame .wpsl-gmap-canvas').css('pointer-events', 'none');

  $('.overlayiFrame').on('click', function onOverlayClick() {
    $(this).find('.wpsl-gmap-canvas').css('pointer-events', 'auto');
  });

  $('.overlayiFrame').on('mouseleave', function onOverlayLeave() {
    $(this).find('.wpsl-gmap-canvas').css('pointer-events', 'none');
  });
};

// ── Init ─────────────────────────────────────────────────────────────────────

const initInviewAnimations = () => {
  const elements = Array.from(document.querySelectorAll(SELECTOR));
  if (!elements.length) {
    return;
  }

  // Pre-apply movement and duration so initial off-screen transforms use the
  // configured values before the first IntersectionObserver callback.
  elements.forEach(prepareElementStyles);

  // Reveal everything immediately when the user prefers reduced motion or
  // IntersectionObserver isn't available (old browsers).
  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;
  if (reduceMotion || typeof IntersectionObserver === 'undefined') {
    elements.forEach(reveal);
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const element = entry.target;
        const once = shouldAnimateOnce(element);

        if (entry.isIntersecting) {
          reveal(element);

          // Stop observing after first reveal unless data-animate-once="false".
          if (once) {
            observer.unobserve(element);
          }

          return;
        }

        // Reset so the animation replays on next intersection.
        if (!once) {
          hide(element);
        }
      });
    },
    {
      root: null,
      threshold: 0.01,
      // Allow large horizontal fly-in offsets without blocking vertical
      // intersection detection.
      rootMargin: '0px 100% -20% 100%',
    },
  );

  elements.forEach((element) => {
    observer.observe(element);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initInviewAnimations();
    setTimeout(initIframeOverlay, 1000);
  });
} else {
  initInviewAnimations();
  setTimeout(initIframeOverlay, 1000);
}
